import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import {
    AdornedInput,
    Adornment,
    Anchor,
    Checkbox,
    FormField,
    FormHelp,
    IconMail,
    IconPhone,
    IconUser,
    Input,
    Label,
    LabeledControl,
    LoadingButton,
    Paragraph,
    Text,
    Tooltip,
    TooltipPopper,
    TriggerText,
} from '@zillow/constellation';
import { RX_EMAIL, RX_PHONE } from '@zillow/validation-regexes';
import { track } from '@zillow/universal-analytics';

import type {
    ContactAgentFormContentProps,
    ContactAgentFormData,
    SubmitContactResult,
} from '../types/@zillow-types';
import { formatCity } from '../utils/addressUtils';
import { getCopy } from '../utils/copyUtils';

const StyledParagraph = styled(Paragraph)<{ centerAlignedNote: boolean }>`
    text-align: ${(props) => (props.centerAlignedNote ? 'center' : 'left')};
`;

const legalTooltip = (
    <Tooltip
        body={
            <Paragraph data-testid="real-estate-professionals-tooltip">
                &ldquo;Real estate professionals&rdquo; includes the real estate agents and brokers,
                mortgage lenders and loan officers, property managers and other professionals you
                interact with through Zillow. Zillow Premier Agent is an advertising program that
                helps connect customers to local real estate professionals. Zillow Premier Agent
                partners are paid advertisers and are not affiliated with Zillow, Inc. brokerage or
                any of its affiliates.
            </Paragraph>
        }
    />
);

const ContactAgentFormContent = ({
    property,
    viewer,
    showCheckBox = true,
    centerAlignedNote = true,
    page,
    location,
    treatment,
    onSubmit,
    onSubmitSuccess,
    zatlasEndpoint,
    contactFormVersion,
    trackingParam,
}: ContactAgentFormContentProps) => {
    const submitButtonText = 'Submit';
    const [disableSubmit, setDisableSubmit] = useState<boolean>(showCheckBox);
    const [submitErrMsg, setSubmitErrMsg] = useState<string>('');
    const [showLoadingButton, setShowLoadingButton] = useState<boolean>(false);
    const checkboxText =
        contactFormVersion === 'sebastian-360' || !property.city
            ? "I'm the owner of this home"
            : `I’m a homeowner in the ${formatCity(property.city)} area`;

    // Known issue in v6: https://github.com/react-hook-form/react-hook-form/issues/2887
    // eslint-disable-next-line zillow/@typescript-eslint/unbound-method
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<ContactAgentFormData>({
        mode: 'onBlur',
        defaultValues: {
            name: viewer?.name,
            email: viewer?.email,
            phone: '',
        },
    });

    const handleSubmitInner = async (formData: ContactAgentFormData) => {
        setShowLoadingButton(true);
        setSubmitErrMsg('');
        const response: SubmitContactResult = await onSubmit({
            property,
            zatlasEndpoint,
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            source: trackingParam,
        });
        track({
            category: 'Homes',
            label: `Submit|${page}|${location}|${treatment}`,
            action: 'Seb 360 Upsell',
        });
        if (response.errors?.submit) {
            setShowLoadingButton(false);
            setSubmitErrMsg(response.errors.submit);
        } else {
            onSubmitSuccess();
        }
    };

    return (
        <React.Fragment>
            <StyledParagraph marginBottom="sm" centerAlignedNote={centerAlignedNote}>
                {getCopy(contactFormVersion, 'formBody')}
            </StyledParagraph>
            <form onSubmit={handleSubmit(handleSubmitInner)}>
                <FormField marginBottom={errors.name ? 'xs' : 'sm'}>
                    <AdornedInput
                        inputId="name"
                        leftAdornment={
                            <Adornment>
                                <IconUser fontColor="textDark" />
                            </Adornment>
                        }
                        input={
                            <Input
                                placeholder="Name"
                                fluid
                                {...register('name', {
                                    required: 'Please enter your name.',
                                })}
                            />
                        }
                    />
                    {errors.name?.message && <FormHelp error>{errors.name.message}</FormHelp>}
                </FormField>
                <FormField marginBottom={errors.phone ? 'xs' : 'sm'}>
                    <AdornedInput
                        inputId="phone"
                        leftAdornment={
                            <Adornment>
                                <IconPhone fontColor="textDark" />
                            </Adornment>
                        }
                        input={
                            <Input
                                data-testid="om-contact-agent-phone-input"
                                placeholder="Phone"
                                fluid
                                {...register('phone', {
                                    required: 'Please enter a valid US phone number.',
                                    pattern: {
                                        value: RX_PHONE as RegExp,
                                        message: 'Please enter a valid US phone number.',
                                    },
                                })}
                            />
                        }
                    />
                    {errors.phone?.message && <FormHelp error>{errors.phone.message}</FormHelp>}
                </FormField>
                <FormField marginBottom={errors.email ? 'xs' : 'sm'}>
                    <AdornedInput
                        inputId="email"
                        leftAdornment={
                            <Adornment>
                                <IconMail fontColor="textDark" />
                            </Adornment>
                        }
                        input={
                            <Input
                                placeholder="Email"
                                type="text"
                                fluid
                                {...register('email', {
                                    required: 'Please enter a valid email address.',
                                    pattern: {
                                        value: RX_EMAIL as RegExp,
                                        message: 'Please enter a valid email address.',
                                    },
                                })}
                            />
                        }
                    />
                    {errors.email?.message && <FormHelp error>{errors.email.message}</FormHelp>}
                </FormField>
                <FormField>
                    {showCheckBox && (
                        <LabeledControl
                            marginBottom="xs"
                            controlId="homeowner"
                            label={<Label>{checkboxText}</Label>}
                            onChange={() => {
                                setDisableSubmit(!disableSubmit);
                            }}
                            control={<Checkbox data-testid="homeowner-checkbox" name="homeowner" />}
                        />
                    )}
                </FormField>
                <FormField marginBottom={submitErrMsg ? 'xs' : 'sm'}>
                    <LoadingButton
                        type="submit"
                        buttonType="primary"
                        fluid
                        disabled={disableSubmit || showLoadingButton}
                        loading={showLoadingButton}
                    >
                        {submitButtonText}
                    </LoadingButton>
                    {submitErrMsg && <FormHelp error>{submitErrMsg}</FormHelp>}
                </FormField>
            </form>
            <Text data-testid="form-legal-terms" fontType="legal" as="p">
                By pressing {submitButtonText}, you agree that Zillow Group and its affiliates, and{' '}
                <TooltipPopper triggered={legalTooltip}>
                    <TriggerText>real estate professionals</TriggerText>
                </TooltipPopper>{' '}
                may call/text you about your inquiry, which may involve use of automated means and
                prerecorded/artificial voices. You don&apos;t need to consent as a condition of
                buying any property, goods or services. Message/data rates may apply. You also agree
                to our <Anchor href="/z/corp/terms">Terms of Use</Anchor>. Zillow does not endorse
                any real estate professionals. We may share information about your recent and future
                site activity with your agent to help them understand what you&apos;re looking for
                in a home.
            </Text>
        </React.Fragment>
    );
};

export default ContactAgentFormContent;
